import React, { useEffect, useState, useCallback } from 'react';
import {
  Slider,
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import API, { createAxiosHeaders } from '../utils/api';

const modalStyle = { direction:"rtl",bgcolor: '#1e1e1e', p: 3, borderRadius: 2 };

// Custom hook to load questions, scores, averages/counts
function useScores(open, leagueId, leaderId) {
  const [questions, setQuestions] = useState([]);
  const [teamScores, setTeamScores] = useState([]);
  const [averages, setAverages] = useState({});
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [qRes, sRes, avgRes] = await Promise.all([
        API.get(`/api/questions/?league=${leagueId}`, createAxiosHeaders()),
        API.get(`/api/team-scores/?leader=${leaderId}`, createAxiosHeaders()),
        API.get(
          `/api/team-scores/averages/?league=${leagueId}&leader=${leaderId}`,
          createAxiosHeaders()
        ),
      ]);
      setQuestions(qRes.data);
      setTeamScores(sRes.data);

      const avgMap = {};
      const cntMap = {};
      avgRes.data.forEach((a) => {
        avgMap[a.question] = a.average_score;
        cntMap[a.question] = a.count;
      });
      setAverages(avgMap);
      setCounts(cntMap);
    } catch {
      setError('خطا در دریافت اطلاعات');
    } finally {
      setLoading(false);
    }
  }, [leagueId, leaderId]);

  useEffect(() => {
    if (open && leagueId != null && leaderId != null) load();
  }, [open, load, leagueId, leaderId]);

  return { questions, teamScores, averages, counts, loading, error, reload: load };
}

function ScoreModal({ open, onClose, team, leagueId, onSubmitted }) {
  const { questions, teamScores, averages, counts, loading, error, reload } =
    useScores(open, leagueId, team?.id);
  const [scores, setScores] = useState({});
  const [submitted, setSubmitted] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // initialize local state whenever data loads
  useEffect(() => {
    if (!loading && open) {
      const initScores = {};
      const initSubmitted = {};
      questions.forEach((q) => {
        initScores[q.id] = q.min_score;
        initSubmitted[q.id] = false;
      });
      teamScores.forEach((s) => {
        initScores[s.question] = s.score;
        initSubmitted[s.question] = true;
      });
      setScores(initScores);
      setSubmitted(initSubmitted);
    }
  }, [loading, questions, teamScores, open]);

  const handleChange = (id, value, min, max) => {
    const num = Number(value);
    if (!isNaN(num) && num >= min && num <= max) {
      setScores((prev) => ({ ...prev, [id]: num }));
    }
  };

    const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = questions
        .filter((q) => !submitted[q.id])
        .map((q) => ({ leader: team.id, question: q.id, score: scores[q.id] }));
      if (!payload.length) {
        setSnackbar({ open: true, message: 'امتیازی برای ثبت وجود ندارد', severity: 'info' });
      } else {
        const response = await API.post(
          '/api/team-scores/bulk/',
          payload,
          createAxiosHeaders()
        );
        setSnackbar({ open: true, message: response.data.detail || 'ثبت موفق', severity: 'success' });
        onSubmitted();
        reload();
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
  dir="rtl"
  open={open}
  onClose={onClose}
  maxWidth="md"
  fullWidth
  PaperProps={{
    className: 'bg-gray-900 text-gray-100 rounded-lg shadow-lg',
  }}
>
  <DialogTitle
    className="flex flex-row items-center justify-between relative bg-gray-900 border-b border-gray-700 py-4 px-6 text-lg font-bold text-gray-100"
  >
   <div> ثبت امتیاز: <strong>{team?.teamname || '—'}</strong></div>
    <IconButton
      edge="end"
      onClick={onClose}
      size="small"
      className="absolute top-0 right-3 "
    >
      <CloseIcon className='text-white hover:text-gray-600 transition-all' />
    </IconButton>
  </DialogTitle>

  <DialogContent
    className="bg-gray-800 text-gray-100 p-6 divide-y divide-gray-700"
    dividers
  >
    {loading ? (
      <div className="flex justify-center py-8">
        <svg
          className="animate-spin h-8 w-8 text-cyan-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8z"
          />
        </svg>
      </div>
    ) : error ? (
      <p className="text-red-500">{error}</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="min-w-full text-gray-100">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="px-4 py-2 text-right">سوال</th>
              <th className="px-4 py-2 text-right">تعداد داوران</th>
              <th className="px-4 py-2 text-right">امتیاز شما</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {questions.map((q) => (
              <tr
                key={q.id}
                className={
                  submitted[q.id] ? 'bg-green-900/30' : 'bg-yellow-900/30'
                }
              >
                <td className="px-4 py-2">
                  <span
                    className={`font-medium ${
                      submitted[q.id] ? 'text-green-400' : 'text-yellow-400'
                    }`}
                  >
                    {q.text}
                  </span>
                </td>
                
                <td className="px-4 py-2">{counts[q.id] || 0}</td>
                <TableCell className="px-4 py-2">
  <Box display="flex" alignItems="center" gap={2}>
    {/* نمایش مقدار فعلی */}
    <Typography variant="body2" color={ submitted[q.id] ? 'success.main' : 'warning.main' }>
      {scores[q.id]}
    </Typography>

    {/* اسلایدر */}
    <Slider
      min={q.min_score}
      max={q.max_score}
      value={scores[q.id] ?? q.min_score}
      onChange={(_, val) =>
        handleChange(q.id, val, q.min_score, q.max_score)
      }
      disabled={submitted[q.id]}
      size="small"
      sx={{
        flex: 1,
        '& .MuiSlider-thumb': {
          width: 12,
          height: 12,
          transition: 'transform 0.2s',
          '&:hover, &.Mui-active': { transform: 'scale(1.1)' }
        },
        '& .MuiSlider-track': { height: 4, borderRadius: 2 },
        '& .MuiSlider-rail':  { height: 4, borderRadius: 2, opacity: 0.3 },
      }}
    />
  </Box>
</TableCell>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </DialogContent>

  <DialogActions className="bg-gray-800 border-t border-gray-700 px-6 py-4">
    <button
      onClick={handleSubmit}
      disabled={
        submitting || (!loading && questions.every((q) => submitted[q.id]))
      }
      className={`w-full px-4 py-2 rounded ${
        submitting
          ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
          : 'bg-cyan-600 text-white hover:bg-cyan-500'
      }`}
    >
      {submitting ? (
        <svg
          className="animate-spin h-5 w-5 inline-block"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8z"
          />
        </svg>
      ) : (
        'ثبت تغییرات'
      )}
    </button>
  </DialogActions>
</Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
}

function ViewModal({ open, onClose, team }) {
  const [scores, setScores] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!open || !team?.id) return;
    API.get(`/api/team-scores/?leader=${team.id}`, createAxiosHeaders()).then((res) => {
      setScores(res.data);
      setTotal(res.data.reduce((sum, s) => sum + s.score, 0));
    });
  }, [open, team]);

  return (
    <Dialog
  open={open}
  onClose={onClose}
  dir='rtl'
  maxWidth="sm"
  fullWidth
  PaperProps={{
    className: 'bg-gray-900 text-gray-100 rounded-lg shadow-lg',
  }}
>
  <DialogTitle className="relative bg-slate-800 flex items-center justify-between border-b border-gray-700 py-4 px-6 text-lg font-bold text-gray-100">
   <div> امتیازات شما به : <strong>{team?.teamname || '—'}</strong></div>
    <IconButton
      edge="end"
      onClick={onClose}
      size="small"
      className="absolute top-3 right-3 "
    >
      <CloseIcon  className='text-gray-100 hover:text-white transition-all'/>
    </IconButton>
  </DialogTitle>

  <DialogContent
    dividers
    className="bg-gray-800 text-gray-100 p-6"
  >
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-700 text-gray-100">
        <thead>
          <tr className="border-b border-gray-700">
            <th className="px-4 py-2 text-right">سوال</th>
            <th className="px-4 py-2 text-center">امتیاز</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {scores.map((s) => (
            <tr key={s.id} className="hover:bg-gray-700">
              <td className="px-4 py-2 text-right">{s.question_text}</td>
              <td className="px-4 py-2 text-center">{s.score}</td>
            </tr>
          ))}
          <tr className="bg-gray-900">
            <td className="px-4 py-2 font-semibold">جمع کل</td>
            <td className="px-4 py-2 font-semibold text-center">{total}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </DialogContent>

  <DialogActions className="bg-gray-800 border-t border-gray-700 px-6 py-4">
    <button
      onClick={onClose}
      className="w-full px-4 py-2 rounded border bg-cyan-600 border-gray-600 text-gray-100 hover:border-gray-500"
    >
      بستن
    </button>
  </DialogActions>
</Dialog>

  );
}

export default function DavarPanel({ userData, leagueData }) {
  const [teams, setTeams] = useState([]);
  const [modal, setModal] = useState({ type: null, team: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await API.get(`/api/teams/?league=${userData.league}`, createAxiosHeaders());
      setTeams(res.data);
    } catch {
      setError('خطا در دریافت لیست تیم‌ها');
    } finally {
      setLoading(false);
    }
  }, [userData.league]);

  useEffect(() => {
    if (userData.league) fetchTeams();
  }, [fetchTeams, userData.league]);

  const openModal = (type, team) => setModal({ type, team });
  const closeModal = () => setModal({ type: null, team: null });

  // guard if leagueData not loaded
  if (!leagueData) {
    return (
      <Box textAlign="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div dir="rtl" className="p-2  text-gray-100 min-h-screen">
      <h1 className="text-md pb-[2px]">لیگ: {leagueData.name}</h1>

      {loading ? (
        <div className="flex justify-center py-16">
          <svg className="animate-spin h-10 w-10 text-cyan-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
        </div>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : (
        <div className="overflow-x-auto bg-gray-800 rounded-md shadow border">
          <table className="min-w-full divide-y divide-gray-700 ">
            <thead>
              <tr className="text-center ">
                <th className="text-sm px-4 py-2">تیم</th>
                <th className="text-sm px-4 py-2 text-center">فایل ارسالی</th>
                <th className="text-sm px-4 py-2 text-right">عملیات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {teams.map((team) => (
                <tr key={team?.id} className="hover:bg-gray-700">
                  <td className="px-4 py-3 text-center">{team?.teamname}</td>
                  <td className="px-4 py-3 text-center">
                    {team.submission_file?.status == "accepted" ? (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${team.submission_file?.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block px-3 py-1 text-sm font-medium bg-cyan-600 hover:bg-cyan-500 rounded"
                      >
                        دانلود
                       
                      </a>
                      
                    ) : (
                      <span className="text-gray-500 text-sm">بارگذاری نشده</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => openModal('view', team)}
                      className="inline-flex items-center p-2 bg-gray-700 hover:bg-gray-600 rounded"
                      title="نمایش امتیازات"
                    >
                      👁️
                    </button>
                    <button
                      onClick={() => openModal('score', team)}
                      className="inline-flex items-center p-2 bg-gray-700 hover:bg-gray-600 rounded"
                      title="ثبت امتیاز"
                    >
                      ✍️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/** مودال‌ها هم بدون تغییر باقی می‌مانند **/}
      <ScoreModal
        open={modal.type === 'score'}
        team={modal.team}
        leagueId={userData.league}
        onClose={closeModal}
        onSubmitted={fetchTeams}
      />
      <ViewModal open={modal.type === 'view'} team={modal.team} onClose={closeModal} />
    </div>
  );
}
